import styled from '@emotion/styled';
import { Popover, Slider, SliderThumb } from '@mui/material';
import { isEqual } from 'lodash';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import tw from 'twin.macro';

import CrossIcon from '../../../assets/svg/CrossIcon';
import { CALL_DURATION } from '../../../constants';
import { secondsToTime } from '../../../helpers';
import { Button } from '../../Shared';

const StyledSlider = styled(Slider)`
  ${tw`py-2 h-2 text-brand-500`}

  .MuiSlider-rail {
    ${tw`text-base-200 opacity-100`}
  }

  & .MuiSlider-thumb {
    ${tw`w-6 h-6`}

    &:hover {
      ${tw`shadow-none`}
    }

    &::after {
      ${tw`!hidden`}
    }
  }

  & .Mui-active {
    ${tw`!shadow-none`}
  }
`;

export const CallDurationFilter = ({
  initialCallDuration,
  callDuration,
  onChangeCallDuration,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [value, setValue] = useState(callDuration);

  const onChangeValue = (_e, nextValue) => {
    setValue(nextValue);
  };

  const onChange = () => {
    onChangeCallDuration(prev => {
      return {
        ...prev,
        callDuration: value,
      };
    });
    onClosePopover();
  };

  const reset = () => {
    onChangeCallDuration(prev => {
      return {
        ...prev,
        callDuration: [CALL_DURATION.MIN, CALL_DURATION.MAX],
      };
    });
  };

  const onOpenPopover = e => {
    if (e.target.localName !== 'button') return;
    setValue(callDuration);
    setAnchorEl(e.currentTarget);
  };

  const onClosePopover = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const hasChanged = !isEqual(callDuration, initialCallDuration);
  const isButtonApplyDisabled = isEqual(callDuration, value);

  return (
    <>
      <button
        className={twMerge(
          'flex items-center gap-2 rounded-full py-[11px] px-6 text-brand-900 border border-brand-900 font-medium text-[16px]/[24px] focus-visible:outline-none',
          hasChanged && 'bg-brand-900 text-white !pl-6 !pr-5',
        )}
        onClick={onOpenPopover}
      >
        {hasChanged
          ? `${secondsToTime(callDuration[0])} - ${secondsToTime(callDuration[1])}`
          : 'Call Duration'}
        {hasChanged && (
          <div onClick={reset}>
            <CrossIcon className="w-4 h-4 text-white" />
          </div>
        )}
      </button>
      <Popover
        open={open}
        slotProps={{
          paper: {
            className:
              '!rounded-xl mt-2 !shadow-[0_4px_20px_0_rgba(17,17,58,0.08)] bg-white w-80',
          },
        }}
        anchorEl={anchorEl}
        onClose={onClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <div className="p-4 flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <div className="w-full border border-brand-300/30 rounded-xl py-[11px] px-4">
              <span className="text-[18px]/[24px] text-base-1000">
                {secondsToTime(value[0])}
              </span>
            </div>
            <span className="text-[18px]/[24px] text-base-600">-</span>
            <div className="w-full border border-brand-300/30 rounded-xl py-[11px] px-4">
              <span className="text-[18px]/[24px] text-base-1000">
                {secondsToTime(value[1])}
              </span>
            </div>
          </div>
          <StyledSlider
            value={value}
            onChange={onChangeValue}
            min={CALL_DURATION.MIN}
            max={CALL_DURATION.MAX}
            step={CALL_DURATION.STEP}
            slots={{
              thumb: ({ children, ...props }) => (
                <SliderThumb {...props}>
                  {children}
                  <div className="bg-white w-6 h-6 border-[1.5px] rounded-full border-brand-500" />
                </SliderThumb>
              ),
            }}
          />
        </div>
        <div className="px-4 py-3 border-t border-t-base-200">
          <Button
            title="Apply"
            onClick={onChange}
            className="ml-auto"
            disabled={isButtonApplyDisabled}
          />
        </div>
      </Popover>
    </>
  );
};
